import { useEffect, useRef } from 'react';
import { HiSparkles, HiShieldCheck } from 'react-icons/hi';
import { FaLock, FaUserShield, FaBalanceScale, FaSyncAlt, FaServer } from 'react-icons/fa';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useHeroEntrance from '../hooks/useHeroEntrance';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const sections = [
  {
    icon: <FaUserShield />,
    title: 'Information We Collect',
    items: [
      'Personal details (name, email, phone number) submitted through contact forms.',
      'Project-related information provided during consultations.',
    ],
  },
  {
    icon: <FaSyncAlt />,
    title: 'How We Use Your Information',
    items: [
      'To respond to your inquiries and provide requested services.',
      'To improve, analyze, and personalize our website experience.',
      'For internal record keeping and legal compliance.',
    ],
  },
  {
    icon: <FaBalanceScale />,
    title: 'Data Sharing',
    body: 'We do not sell or share your personal information with third parties except:',
    items: [
      'When required by law or to comply with legal processes.',
      'With service partners or contractors assisting with our services, bound by confidentiality agreements.',
    ],
  },
  {
    icon: <FaLock />,
    title: 'Data Security',
    body: 'We implement appropriate technical and organizational measures to safeguard your personal data.',
  },
  {
    icon: <FaServer />,
    title: 'Changes to This Policy',
    body: 'We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date.',
  },
];

const PrivacyPolicy = () => {
  const heroBadgeRef = useRef(null);
  const heroH1Ref    = useRef(null);
  const heroParaRef  = useRef(null);
  useHeroEntrance([
    heroBadgeRef,
    { ref: heroH1Ref, heading: true },
    heroParaRef,
  ]);

  /* Section cards: stagger entrance + smooth hover lift */
  const sectionsWrapRef = useRef(null);
  useEffect(() => {
    if (!sectionsWrapRef.current) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.policy-card');
      if (!cards.length) return;
      if (prefersReducedMotion()) {
        gsap.set(cards, { opacity: 1, y: 0, scale: 1 });
        return;
      }
      gsap.set(cards, { opacity: 0, y: 36, scale: 0.97, willChange: 'transform' });
      cards.forEach((c) => {
        ScrollTrigger.create({
          trigger: c,
          start: 'top 88%',
          once: true,
          onEnter: () => gsap.to(c, { opacity: 1, y: 0, scale: 1, duration: 0.85, ease: 'power3.out' }),
        });
        const onEnter = () => gsap.to(c, { y: -6, scale: 1.01, duration: 0.4, ease: 'power3.out', overwrite: 'auto' });
        const onLeave = () => gsap.to(c, { y: 0, scale: 1, duration: 0.6, ease: 'power3.out', overwrite: 'auto' });
        c.addEventListener('mouseenter', onEnter);
        c.addEventListener('mouseleave', onLeave);
      });
    }, sectionsWrapRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative pt-36 pb-20 overflow-hidden bg-paper">
        <div className="absolute inset-0 bg-mesh-soft pointer-events-none" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-nexcess-blue/8 blur-3xl animate-blob pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-nexcess-purple/8 blur-3xl animate-blob pointer-events-none" style={{ animationDelay: '4s' }} />

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div ref={heroBadgeRef} className="badge mb-5 mx-auto">
              <HiSparkles /> Legal · Document 01
            </div>
            <h1 ref={heroH1Ref} className="display-hero mb-6">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p ref={heroParaRef} className="narrative-lg">
              Nexcess Infotech values your privacy. This Policy outlines how we collect,
              use, and safeguard your personal information.
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="section-padding bg-paper-2">
        <div className="container-custom">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-32 bg-white rounded-2xl p-7 border border-hairline shadow-soft">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-nexcess-blue to-nexcess-purple flex items-center justify-center text-white text-xl mb-4 shadow-soft">
                  <HiShieldCheck />
                </div>
                <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-2">On This Page</div>
                <h3 className="heading-sm mb-4">Privacy Policy</h3>
                <ul className="space-y-2 text-sm">
                  {sections.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-ink-soft">
                      <span className="text-nexcess-blue font-semibold">{String(i + 1).padStart(2, '0')}</span>
                      <span>{s.title}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-hairline text-xs text-ink-soft">
                  Last updated · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
              </div>
            </aside>

            {/* Sections */}
            <div ref={sectionsWrapRef} className="lg:col-span-8 space-y-5">
              {sections.map((s, i) => (
                <article
                  key={i}
                  className="policy-card bg-white rounded-2xl p-7 md:p-8 border border-hairline shadow-soft cursor-default"
                  style={{ willChange: 'transform' }}
                >
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-nexcess-blue to-nexcess-cyan flex items-center justify-center text-white text-lg shadow-soft shrink-0">
                      {s.icon}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-1">
                        / {String(i + 1).padStart(2, '0')}
                      </div>
                      <h2 className="heading-sm">{s.title}</h2>
                    </div>
                  </div>
                  {s.body && <p className="narrative-body mb-3">{s.body}</p>}
                  {s.items && (
                    <ul className="space-y-2.5 mt-3">
                      {s.items.map((it, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-ink-soft leading-relaxed">
                          <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-br from-nexcess-blue to-nexcess-purple" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
